import { faTag } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cryptoRandomString from "crypto-random-string";
import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Image,
  Row,
  Spinner,
} from "react-bootstrap";
import { AlertOctagon } from "react-feather";
import toast from "react-hot-toast";

import listingService from "../../../../../services/listingService";
import printService from "../../../../../services/print.service";

import imageNotFound from "../../../../../assets/img/imageNotFound.png";

const AsinList = ({ vendor, setVendor, submitting, setSubmitting }) => {
  const [error, setError] = useState({});
  const [toggle, setToggle] = useState(true);

  const [asin, setAsin] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [validationErrors, setValidationErrors] = useState({});
  const [listing, setListing] = useState({});

  const handleSubmit = async () => {
    setError({});
    setValidationErrors({});
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    // Create an RPN
    const userId = JSON.parse(localStorage.getItem("user"))
      .userID.toString()
      .padStart(2, "0");
    const token = cryptoRandomString({ length: 8, type: "distinguishable" });
    const lpn = `RPN${userId}${token}`;

    const response = await listingService.createAlternateListing({
      listingType: "ASIN",
      lpn,
      asin,
      quantity,
      userId,
      vendorId: vendor.vendorId,
    });

    console.log(response);

    if (response.success) {
      setListing(response.newListing);

      if (!response.newListing.listingId) {
        setSubmitting(false);
        toast.error("Error creating listing, please try again.");
        setError({ message: "Error creating listing, please try again." });
        return;
      }

      const pendingFnsku =
        response.newListing.listingStatus === "PENDING_FNSKU";

      await printService.printListingLabel({
        fnsku: response.newListing.fnsku,
        sku: response.newListing.sku,
        amazonConditionLabel: response.newListing.amazonConditionLabel,
        inspectConditionCode: response.newListing.inspectConditionCode,
        title: response.newListing.title,
        listingAccountId: response.newListing.listingAccountId,
        listingId: response.newListing.listingId,
        userId,
        pendingFnsku,
        quantity: Number(response.newListing.quantity),
      });

      setToggle(false);
      setSubmitting(false);
    } else {
      toast.error("Something went wrong, please try again.");
      setError(response.error);
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    let valid = true;

    // ASIN validation --------------------------------
    if (!asin) {
      setValidationErrors((prevState) => {
        return { ...prevState, asin: "ASIN is required." };
      });
      valid = false;
    } else if (asin.length !== 10) {
      setValidationErrors((prevState) => {
        return { ...prevState, asin: "ASIN must be 10 characters." };
      });
      valid = false;
    }

    // Quantity validation --------------------------------
    if (!quantity) {
      setValidationErrors((prevState) => {
        return { ...prevState, quantity: "Quantity is required." };
      });
      valid = false;
    } else if (!/^\d+$/.test(quantity)) {
      setValidationErrors((prevState) => {
        return { ...prevState, quantity: "Quantity must be digits." };
      });
      valid = false;
    }

    return valid;
  };

  const resetState = () => {
    setAsin("");
    setError({});
    setQuantity(1);
    setListing({});
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-start border-bottom">
        <FontAwesomeIcon icon={faTag} size="3x" className="me-3" />
        <div>
          <Card.Title>ASIN List</Card.Title>
          <Card.Subtitle>
            Enter an ASIN and quantity to create a listing for{" "}
            <strong>{vendor.vendorName}</strong>.
          </Card.Subtitle>
        </div>
      </Card.Header>
      <Card.Body>
        {Object.keys(error).length > 0 && (
          <Alert variant="danger" className="flex-fill alert-blink">
            <div className="alert-icon">
              <AlertOctagon
                className="position-relative top-50 start-50 translate-middle"
                size={20}
              />
            </div>
            <div className="alert-message font-weight-bold">
              {error.message}
            </div>
          </Alert>
        )}
        <Collapse in={toggle}>
          <div>
            <Form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>ASIN</Form.Label>
                    <Form.Control
                      autoFocus
                      size="lg"
                      placeholder="Enter an ASIN..."
                      value={asin}
                      onChange={(e) => setAsin(e.target.value)}
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") {
                          handleSubmit();
                        }
                      }}
                      isInvalid={validationErrors.asin}
                      disabled={submitting}
                    />
                    {validationErrors.asin && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.asin}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={3}>
                  <Form.Group>
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      size="lg"
                      placeholder="Enter a quantity..."
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      isInvalid={validationErrors.quantity}
                      disabled={submitting}
                    />
                    {validationErrors.quantity && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.quantity}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col xs="auto" className="align-self-start">
                  {/* Adds space above the button for validation errors */}
                  <div style={{ height: `${29.45}px` }}></div>
                  <Button
                    size="lg"
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting && (
                      <Spinner
                        className="me-2"
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Col>
              </Row>
            </Form>
            <div className="text-center mt-3">
              <Card.Link
                onClick={() => {
                  if (!submitting) {
                    resetState();
                    setToggle(true);
                    setVendor({});
                  }
                }}
              >
                Select a different vendor
              </Card.Link>
            </div>
          </div>
        </Collapse>
        <Collapse in={!toggle}>
          <div>
            {Object.keys(listing).length > 0 && (
              <>
                <h4>Created Listing</h4>
                <Row className="mt-3">
                  <Col md={3}>
                    <a
                      href={`https://www.amazon.com/dp/${listing.asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        // style={{ maxHeight: "75px" }}
                        fluid
                        thumbnail
                        src={listing.imageLink}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null; // prevents looping
                          currentTarget.src = imageNotFound;
                        }}
                      />
                    </a>
                  </Col>
                  <Col md={9}>
                    {listing.title && (
                      <div className="mb-3">
                        <h5 className="mb-0">Title</h5>
                        <Card.Text>{listing.title}</Card.Text>
                      </div>
                    )}
                    <Row className="mb-3">
                      <Col>
                        <h5 className="mb-0">ASIN</h5>
                        <Card.Text>
                          <a
                            href={`https://www.amazon.com/dp/${listing.asin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {listing.asin}
                          </a>
                        </Card.Text>
                      </Col>
                      <Col>
                        <h5 className="mb-0">FNSKU</h5>
                        <Card.Text>
                          {listing.listingStatus === "PENDING_FNSKU"
                            ? "Pending"
                            : listing.fnsku || "N/A"}
                        </Card.Text>
                      </Col>
                      <Col>
                        <h5 className="mb-0">SKU</h5>
                        <Card.Text>{listing.sku}</Card.Text>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col>
                        <h5 className="mb-0">Listing Account</h5>
                        <Card.Text>{listing.listingAccount}</Card.Text>
                      </Col>
                      <Col>
                        <h5 className="mb-0">Condition</h5>
                        <Card.Text>
                          {listing.amazonConditionLabel ||
                            listing.inspectConditionLabel}
                        </Card.Text>
                      </Col>
                      {listing.foundPrice && (
                        <Col>
                          <h5 className="mb-0">Price</h5>
                          <Card.Text>{listing.foundPrice}</Card.Text>
                        </Col>
                      )}
                      <Col>
                        <h5 className="mb-0">Quantity</h5>
                        <Card.Text>{listing.quantity}</Card.Text>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </>
            )}
            <div className="text-center">
              <Card.Link
                onClick={() => {
                  if (!submitting) {
                    resetState();
                    setToggle(true);
                  }
                }}
              >
                Create a new listing
              </Card.Link>
            </div>
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default AsinList;
